import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../supabaseClient'

function Community() {
  const [users, setUsers] = useState([])
  const [filteredUsers, setFilteredUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [locationFilter, setLocationFilter] = useState('')
  const [sortBy, setSortBy] = useState('username') // username, joined, collection_size
  
  // For location filter dropdown
  const [availableLocations, setAvailableLocations] = useState([])

  useEffect(() => {
    fetchCommunityMembers()
  }, [])

  useEffect(() => {
    filterAndSortUsers()
  }, [searchTerm, locationFilter, sortBy, users])

  const fetchCommunityMembers = async () => {
    try {
      // Fetch all public profiles
      const { data: profilesData, error: profilesError } = await supabase
        .from('public_user_profiles')
        .select('*')
        .eq('profile_is_public', true)
        .order('username')

      if (profilesError) throw profilesError

      // Fetch collection stats for each user
      const { data: statsData, error: statsError } = await supabase
        .from('user_collection_summary')
        .select('*')

      if (statsError) throw statsError

      // Merge profile and stats data
      const usersWithStats = profilesData.map(profile => {
        const stats = statsData?.find(s => s.user_id === profile.id) || {}
        return {
          ...profile,
          total_items: stats.total_items || 0,
          owned_count: stats.owned_count || 0,
          for_trade_count: stats.for_trade_count || 0,
          wishlist_count: stats.wishlist_count || 0
        }
      })

      // Extract unique locations for filter
      const locations = [...new Set(
        usersWithStats
          .filter(u => u.location)
          .map(u => u.location)
      )].sort()

      setUsers(usersWithStats)
      setFilteredUsers(usersWithStats)
      setAvailableLocations(locations)
      setLoading(false)
    } catch (error) {
      console.error('Error fetching community members:', error)
      setLoading(false)
    }
  }

  const filterAndSortUsers = () => {
    let filtered = [...users]

    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase()
      filtered = filtered.filter(user => 
        user.username?.toLowerCase().includes(searchLower) ||
        user.full_name?.toLowerCase().includes(searchLower) ||
        user.bio?.toLowerCase().includes(searchLower)
      )
    }

    // Apply location filter
    if (locationFilter) {
      filtered = filtered.filter(user => user.location === locationFilter)
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'username':
          return (a.username || '').localeCompare(b.username || '')
        case 'joined':
          return new Date(b.created_at) - new Date(a.created_at) // newest first
        case 'collection_size':
          return (b.total_items || 0) - (a.total_items || 0) // largest first
        default:
          return 0
      }
    })

    setFilteredUsers(filtered)
  }

  const clearFilters = () => {
    setSearchTerm('')
    setLocationFilter('')
    setSortBy('username')
  }

  if (loading) {
    return <div className="loading">Loading community members...</div>
  }

  return (
    <div className="community-page">
      <div className="page-header">
        <h1>Community Members</h1>
        <p className="page-subtitle">
          Connect with fellow anglers, discover their collections, and explore trade opportunities
        </p>
      </div>

      {/* Stats Banner */}
      <div className="community-stats-banner">
        <div className="community-stat">
          <span className="stat-number">{users.length}</span>
          <span className="stat-label">Active Members</span>
        </div>
        <div className="community-stat">
          <span className="stat-number">
            {users.reduce((sum, u) => sum + (u.for_trade_count || 0), 0)}
          </span>
          <span className="stat-label">Items For Trade</span>
        </div>
        <div className="community-stat">
          <span className="stat-number">
            {users.reduce((sum, u) => sum + (u.total_items || 0), 0)}
          </span>
          <span className="stat-label">Total Lures Collected</span>
        </div>
      </div>

      {/* Search and Filter Panel */}
      <div className="filters-panel">
        <div className="filter-group">
          <label>Search Members:</label>
          <input
            type="text"
            placeholder="Search by username, name, or bio..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="filter-input"
          />
        </div>

        <div className="filter-group">
          <label>Location:</label>
          <select 
            value={locationFilter} 
            onChange={(e) => setLocationFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Locations</option>
            {availableLocations.map(location => (
              <option key={location} value={location}>
                {location}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Sort By:</label>
          <select 
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value)}
            className="filter-select"
          >
            <option value="username">Username (A-Z)</option>
            <option value="joined">Newest Members</option>
            <option value="collection_size">Largest Collections</option>
          </select>
        </div>

        <button 
          onClick={clearFilters}
          className="clear-filters-btn"
        >
          Clear Filters
        </button>
      </div>

      {/* Results Info */}
      <div className="results-info">
        Showing {filteredUsers.length} of {users.length} members
      </div>

      {/* Members Grid */}
      {filteredUsers.length === 0 ? (
        <div className="no-results">
          <p>No members found matching your search.</p>
          <button onClick={clearFilters} className="btn-primary">
            Clear Filters
          </button>
        </div>
      ) : (
        <div className="members-grid">
          {filteredUsers.map(user => (
            <Link 
              to={`/profile/${user.id}`} 
              key={user.id}
              className="member-card"
            >
              <div className="member-avatar">
                {user.avatar_url ? (
                  <img src={user.avatar_url} alt={user.username} />
                ) : (
                  <div className="avatar-placeholder">
                    {user.username?.charAt(0).toUpperCase() || '?'}
                  </div>
                )}
              </div>

              <div className="member-info">
                <h3 className="member-username">{user.username}</h3>
                {user.full_name && (
                  <p className="member-full-name">{user.full_name}</p>
                )}
                {user.location && (
                  <p className="member-location">📍 {user.location}</p>
                )}
                {user.bio && (
                  <p className="member-bio">
                    {user.bio.length > 100 
                      ? `${user.bio.substring(0, 100)}...` 
                      : user.bio
                    }
                  </p>
                )}
              </div>

              <div className="member-stats">
                <div className="member-stat-item">
                  <span className="member-stat-number">{user.total_items || 0}</span>
                  <span className="member-stat-label">Total</span>
                </div>
                <div className="member-stat-item">
                  <span className="member-stat-number">{user.owned_count || 0}</span>
                  <span className="member-stat-label">Owned</span>
                </div>
                <div className="member-stat-item">
                  <span className="member-stat-number">{user.for_trade_count || 0}</span>
                  <span className="member-stat-label">For Trade</span>
                </div>
              </div>

              <div className="member-footer">
                <span className="member-joined">
                  Member since {new Date(user.created_at).toLocaleDateString()}
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

export default Community
